/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { ROUTING_KEY } from '@app/core/enums/routing-key.enum';
import { SnackbarService } from '@app/core/services';
import { AssistantGuard } from '@app/pages/assistant/guards/assistant.guard';
import { EventResolver } from '@app/pages/event-page/resolvers/event.resolver';
import { GuideResolver } from '@app/pages/guide-page/resolvers/guide.resolver';
import { StaticPageResolver } from '@app/pages/static-pages/resolvers/static-page.resolver';
import { IsCoreAppGuard } from '@app/shared/guards/is-core-app.guard';
import { IsPlacesVisibleGuard } from '@app/shared/guards/is-place-visible.guard';
import { IsWhiteLabelAppGuard } from '@app/shared/guards/is-white-label-app.guard';
import { ClusterResolver } from '@app/shared/resolvers/cluster.resolver';
import {
  BtrEventApiService,
  BtrPassportApiService,
  BtrStaticPageApiService,
  GuideApiService,
} from '@app/shared/services/api';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { CheckInResolver } from './pages/check-in-page/resolvers/check-in.resolver';
import { PassportResolver } from './pages/passport-page/resolvers/passport.resolver';
import { PlaceResolver } from './pages/place-page/resolvers/place.resolver';

const routes: Routes = [
  { path: 'main', redirectTo: '' },
  {
    path: 'redirect',
    loadComponent: () =>
      import('./core/components/redirect/redirect.component').then(
        c => c.RedirectComponent,
      ),
  },
  {
    path: 'redirect/:hash',
    loadComponent: () =>
      import('./core/components/redirect/redirect.component').then(
        c => c.RedirectComponent,
      ),
  },

  {
    path: '',
    pathMatch: 'full',
    providers: [IsPlacesVisibleGuard],
    canActivate: [IsPlacesVisibleGuard],
    loadChildren: () =>
      import('./pages/main-page/main-page.module').then(m => m.MainPageModule),
  },
  {
    path: 's',
    data: {
      page: ROUTING_KEY.STANDALONE_EVENT,
    },
    children: [
      {
        path: 'event',

        children: [
          {
            path: ':eventSlug',
            data: {
              page: ROUTING_KEY.STANDALONE_EVENT,
            },
            providers: [
              EventResolver,
              IsCoreAppGuard,
              ClusterResolver,
              BtrEventApiService,
            ],
            resolve: {
              eventModel: EventResolver,
              clusterModel: ClusterResolver,
            },
            loadComponent: () =>
              import(
                './pages/event-page/components/event-page/event-page.component'
              ).then(c => c.EventPageComponent),
          },
        ],
      },
      {
        path: 'passport',

        children: [
          {
            path: ':passportSlug',
            data: {
              page: ROUTING_KEY.STANDALONE_PASSPORT,
            },
            providers: [
              PassportResolver,
              IsCoreAppGuard,
              ClusterResolver,
              BtrPassportApiService,
            ],
            resolve: {
              passportModel: PassportResolver,
              clusterModel: ClusterResolver,
            },
            loadComponent: () =>
              import(
                './pages/passport-page/components/passport-page/passport-page.component'
              ).then(c => c.PassportPageComponent),
          },
        ],
      },
      {
        path: 'places',
        children: [
          {
            path: ':placeSlug',
            children: [
              {
                path: 'check-in',
                children: [
                  {
                    path: ':token',
                    data: {
                      page: ROUTING_KEY.STANDALONE_PASSPORT_CHECK_IN,
                    },
                    providers: [
                      CheckInResolver,
                      IsCoreAppGuard,
                      ClusterResolver,
                      PlaceResolver,
                      BtrPassportApiService,
                    ],
                    resolve: {
                      checkInInfoModel: CheckInResolver,
                      placeModel: PlaceResolver,
                      clusterModel: ClusterResolver,
                    },
                    loadComponent: () =>
                      import(
                        './pages/check-in-page/components/check-in-page/check-in-page.component'
                      ).then(c => c.CheckInPageComponent),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'destination',
    loadChildren: () =>
      import('./pages/destination-page/destination-page.module').then(
        m => m.DestinationPageModule,
      ),
  },

  {
    path: 'assistant',
    canLoad: [AssistantGuard],
    providers: [AssistantGuard],
    loadChildren: () =>
      import('./pages/assistant/assistant.module').then(m => m.AssistantModule),
  },

  {
    path: 'your-plans',
    loadChildren: () =>
      import('./pages/your-plans-page/your-plans-page.module').then(
        m => m.YourPlansPageModule,
      ),
  },

  {
    path: 'places',
    data: {
      page: ROUTING_KEY.PLACE_LIST,
    },
    providers: [IsPlacesVisibleGuard],
    canActivate: [IsPlacesVisibleGuard],
    loadChildren: () =>
      import('./pages/place-page/place-page.module').then(
        m => m.PlacePageModule,
      ),
  },

  {
    path: 'events',
    data: {
      page: ROUTING_KEY.EVENT_LIST,
    },
    resolve: {
      clusterModel: ClusterResolver,
    },
    providers: [ClusterResolver],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/events-list/events-list.component').then(
            c => c.EventsListComponent,
          ),
      },
      {
        path: ':eventSlug',
        data: {
          page: ROUTING_KEY.EVENT,
        },
        providers: [
          EventResolver,
          ClusterResolver,
          IsWhiteLabelAppGuard,
          BtrEventApiService,
        ],
        resolve: {
          eventModel: EventResolver,
          clusterModel: ClusterResolver,
        },
        loadComponent: () =>
          import(
            './pages/event-page/components/event-page/event-page.component'
          ).then(c => c.EventPageComponent),
        canActivate: [IsWhiteLabelAppGuard],
        children: [
          {
            path: 'place',
            providers: [IsPlacesVisibleGuard],
            canActivate: [IsPlacesVisibleGuard],
            data: {
              page: ROUTING_KEY.FAKE,
            },
            loadChildren: () =>
              import('./pages/place-page/place-page.module').then(
                m => m.PlacePageModule,
              ),
          },
        ],
      },
      {
        path: ':clusterSlug/:eventSlug',
        providers: [
          EventResolver,
          IsCoreAppGuard,
          ClusterResolver,
          BtrEventApiService,
        ],
        data: {
          page: ROUTING_KEY.EVENT,
        },
        loadComponent: () =>
          import(
            './pages/event-page/components/event-page/event-page.component'
          ).then(c => c.EventPageComponent),
        resolve: {
          eventModel: EventResolver,
          clusterModel: ClusterResolver,
        },
        canActivate: [IsCoreAppGuard],
        children: [
          {
            path: 'place',
            loadChildren: () =>
              import('./pages/place-page/place-page.module').then(
                m => m.PlacePageModule,
              ),
          },
        ],
      },
    ],
  },

  {
    path: 'passports',
    data: {
      page: ROUTING_KEY.PASSPORT_LIST,
    },
    resolve: {
      clusterModel: ClusterResolver,
    },
    providers: [ClusterResolver],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/passports-list/passports-list.component').then(
            c => c.PassportsListComponent,
          ),
      },
      {
        path: ':passportSlug',
        data: {
          page: ROUTING_KEY.PASSPORT,
        },
        providers: [
          PassportResolver,
          ClusterResolver,
          IsWhiteLabelAppGuard,
          BtrPassportApiService,
        ],
        resolve: {
          passportModel: PassportResolver,
          clusterModel: ClusterResolver,
        },
        loadComponent: () =>
          import(
            './pages/passport-page/components/passport-page/passport-page.component'
          ).then(c => c.PassportPageComponent),
        canActivate: [IsWhiteLabelAppGuard],
        children: [
          {
            path: 'place',
            data: {
              page: ROUTING_KEY.FAKE,
            },
            loadChildren: () =>
              import('./pages/place-page/place-page.module').then(
                m => m.PlacePageModule,
              ),
          },
        ],
      },
      {
        path: ':clusterSlug/:passportSlug',
        providers: [
          PassportResolver,
          IsCoreAppGuard,
          ClusterResolver,
          BtrPassportApiService,
        ],
        data: {
          page: ROUTING_KEY.PASSPORT,
        },
        resolve: {
          passportModel: PassportResolver,
          clusterModel: ClusterResolver,
        },
        loadComponent: () =>
          import(
            './pages/passport-page/components/passport-page/passport-page.component'
          ).then(c => c.PassportPageComponent),
        canActivate: [IsCoreAppGuard],
        children: [
          {
            path: 'place',
            loadChildren: () =>
              import('./pages/place-page/place-page.module').then(
                m => m.PlacePageModule,
              ),
          },
        ],
      },
    ],
  },

  {
    path: 'deals',
    loadChildren: () =>
      import('./pages/deal-page/deal-page.module').then(m => m.DealPageModule),
  },

  {
    path: 'collections',
    loadChildren: () =>
      import('./pages/category-page/category-page.module').then(
        m => m.CategoryPageModule,
      ),
  },

  {
    path: 'guides',
    data: {
      page: ROUTING_KEY.GUIDE_LIST,
    },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./pages/guides-page/guides-page.module').then(
            m => m.GuidesPageModule,
          ),
      },
      {
        path: ':guideSlug',
        data: {
          page: ROUTING_KEY.GUIDE,
        },
        providers: [
          GuideResolver,
          IsCoreAppGuard,
          ClusterResolver,
          IsWhiteLabelAppGuard,
          GuideApiService,
          SnackbarService,
        ],
        resolve: {
          guideModel: GuideResolver,
          clusterModel: ClusterResolver,
        },
        loadComponent: () =>
          import(
            './pages/guide-page/components/guide-page/guide-page.component'
          ).then(c => c.GuidePageComponent),
        children: [
          {
            path: 'place',
            data: {
              page: ROUTING_KEY.FAKE,
            },
            loadChildren: () =>
              import('./pages/place-page/place-page.module').then(
                m => m.PlacePageModule,
              ),
          },
        ],
      },
    ],
  },

  {
    path: 'static-pages',
    data: {
      page: ROUTING_KEY.STATIC_PAGE,
    },
    children: [
      {
        path: ':staticPageSlug',
        providers: [
          StaticPageResolver,
          IsCoreAppGuard,
          ClusterResolver,
          IsWhiteLabelAppGuard,
          BtrStaticPageApiService,
        ],
        data: {
          page: ROUTING_KEY.STATIC_PAGE,
        },
        resolve: {
          staticPageModel: StaticPageResolver,
          clusterModel: ClusterResolver,
        },
        loadComponent: () =>
          import('./pages/static-pages/components/static-pages.component').then(
            c => c.StaticPagesComponent,
          ),
      },
    ],
  },

  {
    path: 'privacy-policy',
    loadChildren: () =>
      import('./pages/privacy-policy-page/privacy-policy-page.module').then(
        m => m.PrivacyPolicyPageModule,
      ),
  },

  {
    path: 'terms-of-service',
    loadChildren: () =>
      import('./pages/terms-of-service-page/terms-of-service-page.module').then(
        m => m.TermsOfServicePageModule,
      ),
  },

  {
    path: 'your-tickets',

    loadChildren: () =>
      import('./pages/your-tickets-page/your-tickets-page.module').then(
        m => m.YourTicketsPageModule,
      ),
  },
  {
    path: 'your-passports',
    data: {
      page: ROUTING_KEY.YOUR_PASSPORT_LIST,
    },
    loadChildren: () =>
      import('./pages/your-passports-page/your-passports-page.module').then(
        m => m.YourPassportsPageModule,
      ),
  },
  {
    path: 'subscribe',
    data: { page: ROUTING_KEY.RESUBSCRIBE_REMINDER },
    loadChildren: () =>
      import('./pages/reminder-subscription/reminder-subscription.module').then(
        m => m.ReminderSubscriptionModule,
      ),
  },
  {
    path: 'unsubscribe',
    data: { page: ROUTING_KEY.UNSUBSCRIBE_REMINDER },
    loadChildren: () =>
      import('./pages/reminder-subscription/reminder-subscription.module').then(
        m => m.ReminderSubscriptionModule,
      ),
  },
  { path: 'page-not-found', component: PageNotFoundComponent },

  { path: '**', redirectTo: 'page-not-found' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      initialNavigation: 'enabledBlocking',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
